/* =====================================================================
   Host Honest — Theme-Interaktionen
   Dark/Light-Umschalter, Navigation, Reveal-Animationen, Game-Karten
   ===================================================================== */

(function () {
	"use strict";

	var root = document.documentElement;
	var STORAGE_KEY = "hh-theme";

	/* ---------------- Theme ---------------- */

	function currentTheme() {
		return root.getAttribute("data-theme") === "light" ? "light" : "dark";
	}

	function applyTheme(theme, persist) {
		root.setAttribute("data-theme", theme);
		root.style.colorScheme = theme;

		var meta = document.querySelector('meta[name="theme-color"]');
		if (meta) {
			meta.setAttribute("content", theme === "light" ? "#ffffff" : "#0b0c0e");
		}

		document.querySelectorAll("[data-theme-toggle]").forEach(function (btn) {
			btn.setAttribute("aria-pressed", theme === "light" ? "true" : "false");
			btn.setAttribute(
				"aria-label",
				theme === "light" ? "Dunkles Design aktivieren" : "Helles Design aktivieren"
			);
		});

		if (persist) {
			try {
				window.localStorage.setItem(STORAGE_KEY, theme);
			} catch (e) {
				/* Speicher nicht verfügbar — Auswahl gilt nur für diese Seite */
			}
		}

		document.dispatchEvent(
			new CustomEvent("hh:themechange", { detail: { theme: theme } })
		);
	}

	function initTheme() {
		applyTheme(currentTheme(), false);

		document.addEventListener("click", function (event) {
			var btn = event.target.closest("[data-theme-toggle]");
			if (!btn) return;
			event.preventDefault();
			applyTheme(currentTheme() === "light" ? "dark" : "light", true);
		});
	}

	/* ---------------- Scroll-Schwellen ---------------- */

	// Ruft `callback(true)`, sobald die Seite weiter als `offset` Pixel gescrollt
	// ist, und `callback(false)` beim Zurückscrollen. Beobachtet wird ein
	// unsichtbarer Messpunkt an dieser Stelle: Anders als `scrollY` erzwingt der
	// IntersectionObserver kein Layout mitten im Skript.
	function onScrollPast(offset, callback) {
		if (!("IntersectionObserver" in window)) return;

		var marker = document.createElement("div");
		marker.setAttribute("aria-hidden", "true");
		marker.style.cssText =
			"position:absolute;top:" +
			offset +
			"px;left:0;width:1px;height:1px;visibility:hidden;pointer-events:none";
		document.body.appendChild(marker);

		new IntersectionObserver(function (entries) {
			var entry = entries[entries.length - 1];
			callback(!entry.isIntersecting && entry.boundingClientRect.top < 0);
		}).observe(marker);
	}

	/* ---------------- Navigation ---------------- */

	function initNavScroll() {
		var nav = document.querySelector("[data-nav]");
		if (!nav) return;

		onScrollPast(8, function (past) {
			nav.classList.toggle("is-scrolled", past);
		});
	}

	/* ---------------- Nach-Oben-Button ---------------- */

	function initToTop() {
		var btn = document.querySelector("[data-to-top]");
		if (!btn) return;

		onScrollPast(400, function (past) {
			btn.classList.toggle("is-visible", past);
		});

		btn.addEventListener("click", function () {
			window.scrollTo({ top: 0, left: 0 });
		});
	}

	function initDropdowns() {
		var items = Array.prototype.slice.call(
			document.querySelectorAll("[data-dropdown]")
		);
		if (!items.length) return;

		var closeTimer = null;

		function closeAll(except) {
			items.forEach(function (item) {
				if (item === except) return;
				item.classList.remove("is-open");
				var trigger = item.querySelector("[data-dropdown-trigger]");
				if (trigger) trigger.setAttribute("aria-expanded", "false");
			});
		}

		function open(item) {
			window.clearTimeout(closeTimer);
			closeAll(item);
			item.classList.add("is-open");
			var trigger = item.querySelector("[data-dropdown-trigger]");
			if (trigger) trigger.setAttribute("aria-expanded", "true");
		}

		function close(item) {
			item.classList.remove("is-open");
			var trigger = item.querySelector("[data-dropdown-trigger]");
			if (trigger) trigger.setAttribute("aria-expanded", "false");
		}

		items.forEach(function (item) {
			var trigger = item.querySelector("[data-dropdown-trigger]");

			item.addEventListener("mouseenter", function () {
				if (window.matchMedia("(hover: none)").matches) return;
				open(item);
			});

			item.addEventListener("mouseleave", function () {
				if (window.matchMedia("(hover: none)").matches) return;
				closeTimer = window.setTimeout(function () {
					close(item);
				}, 140);
			});

			if (trigger) {
				// Auf Touch-Geräten öffnet der erste Tap das Menü, der zweite folgt
				// dem Link. Mit Maus öffnet der Hover, der Klick navigiert direkt.
				trigger.addEventListener("click", function (event) {
					if (!window.matchMedia("(hover: none)").matches) return;
					if (item.classList.contains("is-open")) return;
					event.preventDefault();
					open(item);
				});
			}

			item.addEventListener("focusin", function () {
				open(item);
			});

			item.addEventListener("focusout", function (event) {
				if (!item.contains(event.relatedTarget)) close(item);
			});
		});

		document.addEventListener("keydown", function (event) {
			if (event.key !== "Escape") return;
			closeAll(null);
		});

		document.addEventListener("click", function (event) {
			if (event.target.closest("[data-dropdown]")) return;
			closeAll(null);
		});
	}

	function initDrawer() {
		var toggle = document.querySelector("[data-drawer-toggle]");
		var drawer = document.querySelector("[data-drawer]");
		if (!toggle || !drawer) return;

		function setOpen(open) {
			drawer.classList.toggle("is-open", open);
			document.body.classList.toggle("hh-nav-open", open);
			toggle.setAttribute("aria-expanded", open ? "true" : "false");
			toggle.setAttribute("aria-label", open ? "Menü schließen" : "Menü öffnen");
			toggle.querySelectorAll("[data-burger-icon]").forEach(function (icon) {
				var showWhenOpen = icon.getAttribute("data-burger-icon") === "close";
				icon.style.display = showWhenOpen === open ? "" : "none";
			});
		}

		setOpen(false);

		toggle.addEventListener("click", function () {
			setOpen(!drawer.classList.contains("is-open"));
		});

		drawer.addEventListener("click", function (event) {
			var top = event.target.closest("[data-drawer-top]");
			if (top) {
				var group = top.closest(".hh-drawer__group");
				var isOpen = group.classList.toggle("is-open");
				top.setAttribute("aria-expanded", isOpen ? "true" : "false");
				return;
			}
			if (event.target.closest("a")) setOpen(false);
		});

		document.addEventListener("keydown", function (event) {
			if (event.key === "Escape") setOpen(false);
		});

		window.addEventListener("resize", function () {
			if (window.innerWidth > 960) setOpen(false);
		});
	}

	/* ---------------- Reveal ---------------- */

	// `.no-js` aus dem Kopf von hh_base.html zeigt alle Reveal-Elemente, bis der
	// erste Observer-Durchlauf sie einsortiert: Was dann schon im Bild ist,
	// bleibt ohne Animation stehen, der Rest blendet beim Scrollen ein.
	function initReveal() {
		var nodes = document.querySelectorAll(".hh-reveal");
		var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		if (!nodes.length || reduced || !("IntersectionObserver" in window)) {
			nodes.forEach(function (node) {
				node.classList.add("is-in");
			});
			root.classList.remove("no-js");
			return;
		}

		var initial = true;
		var observer = new IntersectionObserver(
			function (entries) {
				entries.forEach(function (entry) {
					var box = entry.boundingClientRect;
					var visible =
						entry.isIntersecting ||
						(initial && box.height > 0 && box.top < window.innerHeight);
					if (!visible) return;
					entry.target.classList.add("is-in");
					observer.unobserve(entry.target);
				});

				// Im selben Durchlauf wie `is-in`, damit sichtbare Elemente keine
				// Transition starten.
				if (initial) {
					initial = false;
					root.classList.remove("no-js");
				}
			},
			{ rootMargin: "0px 0px -8% 0px", threshold: 0.08 }
		);

		nodes.forEach(function (node) {
			var group = node.closest("[data-reveal-group]");
			if (group && !node.style.getPropertyValue("--hh-delay")) {
				var siblings = Array.prototype.slice.call(group.querySelectorAll(".hh-reveal"));
				var position = siblings.indexOf(node);
				node.style.setProperty("--hh-delay", Math.min(position, 6) * 70 + "ms");
			}
			observer.observe(node);
		});
	}

	/* ---------------- Gemeinsame Helfer ---------------- */

	// Datensparmodus des Browsers. Videos wiegen hier Megabyte – wer ihn
	// eingeschaltet hat, bekommt nur das poster-Bild.
	function savesData() {
		var conn =
			navigator.connection || navigator.mozConnection || navigator.webkitConnection;
		return !!(conn && conn.saveData);
	}

	// Führt die Funktion aus, sobald die Seite fertig geladen ist und der
	// Hauptstrang Luft hat.
	function whenIdle(fn) {
		function schedule() {
			if (window.requestIdleCallback) {
				window.requestIdleCallback(fn, { timeout: 3000 });
			} else {
				window.setTimeout(fn, 300);
			}
		}

		if (document.readyState === "complete") {
			schedule();
		} else {
			window.addEventListener("load", schedule, { once: true });
		}
	}

	/* ---------------- Game-Karten ---------------- */

	function initGameCards() {
		var cards = document.querySelectorAll("[data-game-card]");
		if (!cards.length) return;

		var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
		var coarse = window.matchMedia("(hover: none)").matches;
		if (reduced || coarse || savesData()) return;

		cards.forEach(function (card) {
			var video = card.querySelector("video");
			if (!video) return;

			video.muted = true;
			video.loop = true;
			video.playsInline = true;

			function play() {
				if (!video.dataset.loaded) {
					video.dataset.loaded = "1";
					video.load();
				}
				var attempt = video.play();
				if (attempt && attempt.catch) attempt.catch(function () {});
				card.classList.add("is-playing");
			}

			function stop() {
				card.classList.remove("is-playing");
				video.pause();
			}

			card.addEventListener("mouseenter", play);
			card.addEventListener("focusin", play);
			card.addEventListener("mouseleave", stop);
			card.addEventListener("focusout", function (event) {
				if (!card.contains(event.relatedTarget)) stop();
			});
		});
	}

	/* ---------------- Hero-Video ---------------- */

	function initHeroVideo() {
		var video = document.querySelector(".hh-hero__video");
		if (!video) return;

		// Kein autoplay-Attribut im Markup, damit reduzierte Bewegung
		// respektiert wird: Browser starten das Video sonst schon, bevor
		// dieses Skript prüfen kann.
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		if (savesData()) return;

		// Das Markup steht auf preload="none": erst dieser Aufruf lädt die
		// Datei. Bis dahin zeigt das poster-Bild, und Schrift, Stylesheets und
		// Bilder haben die Leitung für sich.
		whenIdle(function () {
			var attempt = video.play();
			if (attempt && attempt.catch) attempt.catch(function () {});
		});
	}

	/* ---------------- Gameserver-Übersicht ---------------- */

	function initGamesDirectory() {
		document.querySelectorAll("[data-games-app]").forEach(function (app) {
			var searchInput = app.querySelector("[data-games-search]");
			var searchWrap = searchInput ? searchInput.closest(".hh-search") : null;
			var clearBtn = app.querySelector("[data-games-clear]");
			var viewButtons = Array.prototype.slice.call(
				app.querySelectorAll("[data-games-view]")
			);
			var gridWrap = app.querySelector("[data-games-grid]");
			var listWrap = app.querySelector("[data-games-list]");
			var gridItems = gridWrap ? Array.prototype.slice.call(gridWrap.children) : [];
			var listItems = listWrap ? Array.prototype.slice.call(listWrap.children) : [];
			var emptyEl = app.querySelector("[data-games-empty]");
			var pagination = app.querySelector("[data-games-pagination]");
			var prevBtn = app.querySelector("[data-games-prev]");
			var nextBtn = app.querySelector("[data-games-next]");
			var currentEl = app.querySelector("[data-games-page-current]");
			var totalEl = app.querySelector("[data-games-page-total]");
			var perPage = parseInt(app.getAttribute("data-per-page"), 10) || 9;
			// Beim Blättern zur Überschrift scrollen, nicht zum Eyebrow darüber
			// oder zum Sektionsanfang (siehe .hh-h2 { scroll-margin-top }) –
			// abschaltbar über das Baustein-Feld "scroll_on_page_change".
			var scrollOnPageChange = app.getAttribute("data-games-scroll") !== "0";
			var scrollTarget = app.querySelector(".hh-h2") || app.querySelector(".hh-gamesbar") || app;

			if (!gridItems.length) return;

			var view = "grid";
			var page = 1;

			// gridItems und listItems stammen aus derselben, gleich sortierten
			// Liste – die Trefferliste gilt deshalb für beide Ansichten.
			function matches(query) {
				return gridItems.map(function (item) {
					var name = item.getAttribute("data-name") || "";
					return !query || name.indexOf(query) !== -1;
				});
			}

			function render() {
				var query = searchInput ? searchInput.value.trim().toLowerCase() : "";
				var matchFlags = matches(query);
				var totalMatches = matchFlags.filter(Boolean).length;
				var totalPages = Math.max(1, Math.ceil(totalMatches / perPage));
				if (page > totalPages) page = totalPages;

				var start = (page - 1) * perPage;
				var end = start + perPage;
				var activeItems = view === "list" ? listItems : gridItems;
				var inactiveItems = view === "list" ? gridItems : listItems;

				inactiveItems.forEach(function (item) {
					item.style.display = "none";
				});

				var seen = 0;
				activeItems.forEach(function (item, i) {
					if (!matchFlags[i]) {
						item.style.display = "none";
						return;
					}
					item.style.display = seen >= start && seen < end ? "" : "none";
					seen++;
				});

				if (gridWrap) gridWrap.hidden = view !== "grid";
				if (listWrap) listWrap.hidden = view !== "list";
				if (emptyEl) emptyEl.hidden = totalMatches !== 0;
				if (pagination) pagination.hidden = totalMatches === 0 || totalPages <= 1;
				if (currentEl) currentEl.textContent = page;
				if (totalEl) totalEl.textContent = totalPages;
				if (prevBtn) prevBtn.disabled = page <= 1;
				if (nextBtn) nextBtn.disabled = page >= totalPages;
				if (searchWrap) searchWrap.classList.toggle("has-value", Boolean(query));
			}

			if (searchInput) {
				searchInput.addEventListener("input", function () {
					page = 1;
					render();
				});
			}

			if (clearBtn) {
				clearBtn.addEventListener("click", function () {
					if (!searchInput) return;
					searchInput.value = "";
					searchInput.focus();
					page = 1;
					render();
				});
			}

			viewButtons.forEach(function (btn) {
				btn.addEventListener("click", function () {
					if (btn.classList.contains("is-active")) return;
					view = btn.getAttribute("data-games-view") === "list" ? "list" : "grid";
					viewButtons.forEach(function (other) {
						var active = other === btn;
						other.classList.toggle("is-active", active);
						other.setAttribute("aria-pressed", active ? "true" : "false");
					});
					render();
				});
			});

			if (prevBtn) {
				prevBtn.addEventListener("click", function () {
					if (page <= 1) return;
					page -= 1;
					render();
					if (scrollOnPageChange) {
						scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });
					}
				});
			}

			if (nextBtn) {
				nextBtn.addEventListener("click", function () {
					page += 1;
					render();
					if (scrollOnPageChange) {
						scrollTarget.scrollIntoView({ behavior: "smooth", block: "start" });
					}
				});
			}

			render();
		});
	}

	/* ---------------- Slider ---------------- */

	function initSlider() {
		document.querySelectorAll("[data-slider]").forEach(function (slider) {
			var slides = Array.prototype.slice.call(
				slider.querySelectorAll("[data-slider-slide]")
			);
			var tabs = Array.prototype.slice.call(
				slider.querySelectorAll("[data-slider-tab]")
			);
			if (slides.length < 2 || tabs.length !== slides.length) return;

			var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
			var duration = parseInt(slider.getAttribute("data-interval"), 10) || 6000;

			tabs.forEach(function (tab) {
				tab.style.setProperty("--hh-slider-duration", duration + "ms");
			});

			function goTo(index) {
				slides.forEach(function (slide, i) {
					slide.classList.toggle("is-active", i === index);
				});
				tabs.forEach(function (tab, i) {
					tab.classList.toggle("is-active", i === index);
					tab.setAttribute("aria-selected", i === index ? "true" : "false");
				});
			}

			tabs.forEach(function (tab, index) {
				tab.addEventListener("click", function () {
					if (tab.classList.contains("is-active")) return;
					goTo(index);
				});

				// Die Fortschrittsleiste treibt den automatischen Wechsel an –
				// pausiert sie per Hover (siehe CSS), bleibt auch der Wechsel aus.
				var fill = tab.querySelector(".hh-slider__tab-bar-fill");
				if (!fill) return;

				fill.addEventListener("animationend", function (event) {
					if (reduced) return;
					if (event.animationName !== "hh-slider-progress") return;
					if (!tab.classList.contains("is-active")) return;
					goTo((index + 1) % tabs.length);
				});
			});
		});
	}

	/* ---------------- Hosting-Pakete ---------------- */

	// Gleiche Formatierung wie pricing.format_price auf dem Server; das
	// W\u00e4hrungszeichen kommt von dort \u00fcber data-price-symbol.
	function formatPrice(value, symbol) {
		return (
			value.toLocaleString("de-DE", {
				minimumFractionDigits: 2,
				maximumFractionDigits: 2
			}) +
			" " +
			(symbol || "\u20ac")
		);
	}

	function initPlans() {
		document.querySelectorAll("[data-plan]").forEach(function (plan) {
			var select = plan.querySelector("[data-plan-location]");
			var link = plan.querySelector("[data-order-link]");
			var amountEl = plan.querySelector("[data-price-amount]");
			var template = plan.getAttribute("data-order-template") || "";
			var base = amountEl ? parseFloat(amountEl.getAttribute("data-price-amount")) : NaN;
			var symbol = amountEl ? amountEl.getAttribute("data-price-symbol") : "";

			function update() {
				var option = select ? select.options[select.selectedIndex] : null;
				var value = option ? option.value : "";
				var surcharge = option
					? parseFloat(option.getAttribute("data-surcharge") || "0")
					: 0;

				if (link && template) {
					link.href = template.replace("__LOC__", encodeURIComponent(value));
				}

				if (amountEl && !isNaN(base)) {
					amountEl.textContent = formatPrice(
						base + (isNaN(surcharge) ? 0 : surcharge),
						symbol
					);
				}
			}

			// Ohne Auswahl bleibt der Platzhalter im Link stehen
			if (link && template && !select) {
				link.href = template.replace("__LOC__", "");
			}

			if (select) {
				select.addEventListener("change", update);
				update();
			}
		});
	}

	/* ---------------- Importierte Inhalte aufräumen ---------------- */

	function enhanceProse() {
		document.querySelectorAll(".hh-prose table").forEach(function (table) {
			if (table.parentElement.classList.contains("hh-tablewrap")) return;
			var wrap = document.createElement("div");
			wrap.className = "hh-tablewrap";
			table.parentNode.insertBefore(wrap, table);
			wrap.appendChild(table);
		});

		document.querySelectorAll(".hh-prose img").forEach(function (img) {
			if (!img.getAttribute("loading")) img.setAttribute("loading", "lazy");
			if (!img.getAttribute("decoding")) img.setAttribute("decoding", "async");
		});

		document
			.querySelectorAll('.hh-prose a[href^="http"]')
			.forEach(function (link) {
				if (link.hostname === window.location.hostname) return;
				link.setAttribute("rel", "noopener noreferrer");
			});
	}

	/* ---------------- Start ---------------- */

	function ensureMainTarget() {
		if (document.getElementById("hh-main")) return;
		var fallback = document.querySelector(".page-content-wrapper, .page_content");
		if (fallback) fallback.id = "hh-main";
	}

	function boot() {
		ensureMainTarget();
		initTheme();
		initNavScroll();
		initToTop();
		initDropdowns();
		initDrawer();
		initReveal();
		initHeroVideo();
		initGameCards();
		initGamesDirectory();
		initSlider();
		initPlans();
		enhanceProse();
	}

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", boot);
	} else {
		boot();
	}
})();
